/**
 * Page-based pagination dots for a resource-list carousel.
 *
 * The Horizon slideshow renders one dot per slide, which does not map cleanly to
 * a carousel that shows several cards at once (the trailing dots are never
 * reachable). This element renders one dot per *page* instead, where a page is a
 * group of the currently visible cards. It adapts on resize, so mobile (one card
 * per view) shows one dot per card and desktop (four-up) shows one dot per group.
 */
class ResourceListPagination extends HTMLElement {
  connectedCallback() {
    this.slideshow = this.closest('slideshow-component');
    if (!this.slideshow) return;

    this.list = document.createElement('div');
    this.list.className = 'resource-list__page-dots';
    this.appendChild(this.list);

    this.slideshow.addEventListener('slideshow:select', this.#onSelect);

    // Track the scroll position directly rather than relying on the slideshow's
    // `current` index. With start-aligned, peeking cards the shared slideshow
    // briefly reports the previous slide mid-scroll, which made the active dot
    // flash back to the first and the second dot feel unresponsive. Scroll
    // position is accurate and monotonic.
    this.#scroller = this.slideshow.querySelector('slideshow-slides');
    this.#scroller?.addEventListener('scroll', this.#onScroll, { passive: true });

    this.#resizeObserver = new ResizeObserver(() => this.#render());
    this.#resizeObserver.observe(this.slideshow);

    // The slideshow's slide refs are populated asynchronously after it
    // initialises, so render now (counting slides straight from the DOM) and
    // again on the next frame once layout/refs have settled.
    this.#render();
    requestAnimationFrame(() => this.#render());
  }

  disconnectedCallback() {
    this.slideshow?.removeEventListener('slideshow:select', this.#onSelect);
    this.#scroller?.removeEventListener('scroll', this.#onScroll);
    if (this.#scrollRaf !== null) cancelAnimationFrame(this.#scrollRaf);
    this.#resizeObserver?.disconnect();
  }

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {HTMLElement | null} */
  #scroller = null;

  /** @type {number | null} */
  #scrollRaf = null;

  #onScroll = () => {
    if (this.#scrollRaf !== null) return;
    this.#scrollRaf = requestAnimationFrame(() => {
      this.#scrollRaf = null;
      this.#updateActive();
    });
  };

  get #total() {
    // Count the carousel's own slides from the DOM. These are present as soon as
    // the markup is parsed, unlike the slideshow's `slides` refs which are set up
    // asynchronously. The class is only on this carousel's slides, not the nested
    // product-card galleries.
    return this.slideshow?.querySelectorAll('.resource-list__slide').length || 0;
  }

  get #perPage() {
    // Derive how many cards make up a "page" from the actual layout: the scroll
    // viewport width divided by one card (plus gap). This is stable (it doesn't
    // shrink at the end of the carousel like visibleSlides) and correct on both
    // breakpoints without relying on matchMedia.
    const slides = this.slideshow?.querySelectorAll('.resource-list__slide');
    const scroller = this.slideshow?.querySelector('slideshow-slides');
    if (slides?.length && scroller) {
      const slideWidth = slides[0].getBoundingClientRect().width;
      const gap = parseFloat(getComputedStyle(scroller).columnGap) || 0;
      const viewport = scroller.clientWidth;
      if (slideWidth > 0 && viewport > 0) {
        return Math.max(1, Math.round(viewport / (slideWidth + gap)));
      }
    }

    // Fallbacks before the carousel has laid out.
    if (window.matchMedia('(max-width: 749px)').matches) return 1;
    const columns = parseInt(getComputedStyle(this.slideshow).getPropertyValue('--column-count'), 10);
    return Number.isFinite(columns) && columns > 0 ? columns : 1;
  }

  get #pageCount() {
    return Math.max(1, Math.ceil(this.#total / this.#perPage));
  }

  #render() {
    if (!this.list) return;

    const pageCount = this.#pageCount;
    this.hidden = pageCount <= 1;

    if (this.list.childElementCount !== pageCount) {
      this.list.replaceChildren();
      for (let i = 0; i < pageCount; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'resource-list__page-dot';
        dot.setAttribute('aria-label', `Go to group ${i + 1} of ${pageCount}`);
        dot.addEventListener('click', () => this.#goTo(i));
        this.list.appendChild(dot);
      }
    }

    this.#updateActive();
  }

  #goTo(page) {
    const perPage = this.#perPage;
    const target = Math.min(page * perPage, Math.max(0, this.#total - perPage));
    this.slideshow?.select(target);
  }

  #onSelect = () => {
    this.#updateActive();
  };

  #updateActive() {
    if (!this.list) return;

    const lastPage = this.#pageCount - 1;
    let page = this.#activePageFromScroll();

    // Fall back to the slideshow's index if the scroll geometry isn't ready yet.
    if (page == null) {
      const perPage = this.#perPage;
      const current = this.slideshow?.current || 0;
      page = current >= this.#total - perPage ? lastPage : Math.floor(current / perPage);
    }

    page = Math.min(Math.max(page, 0), lastPage);

    Array.from(this.list.children).forEach((dot, index) => {
      if (index === page) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  /**
   * Active page derived from the scroller's position: scrollLeft divided by the
   * width of one page (perPage cards). Robust against the slideshow's mid-scroll
   * index wobble. Snaps to the last page once scrolled to the end. Returns null
   * when the geometry isn't measurable yet.
   * @returns {number | null}
   */
  #activePageFromScroll() {
    const scroller = this.#scroller;
    const slides = this.slideshow?.querySelectorAll('.resource-list__slide');
    if (!scroller || !slides?.length) return null;

    const slideWidth = slides[0].getBoundingClientRect().width;
    const gap = parseFloat(getComputedStyle(scroller).columnGap) || 0;
    const pageStride = (slideWidth + gap) * this.#perPage;
    if (pageStride <= 0) return null;

    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    if (maxScroll - scroller.scrollLeft <= 1) return this.#pageCount - 1;

    return Math.round(scroller.scrollLeft / pageStride);
  }
}

if (!customElements.get('resource-list-pagination')) {
  customElements.define('resource-list-pagination', ResourceListPagination);
}
